"use client";

import * as React from "react";
import Link from "next/link";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Users } from "lucide-react";
import { UserAvatar } from "@/components/ui/user-avatar";

interface UserMenuProps {
  displayName: string;
  isAdmin?: boolean;
  avatarUrl?: string | null;
}

export function UserMenu({ displayName, isAdmin, avatarUrl }: UserMenuProps) {
  const formRef = React.useRef<HTMLFormElement | null>(null);
  const switchAccountFormRef = React.useRef<HTMLFormElement | null>(null);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background px-1.5 py-1.5 text-sm font-medium text-foreground shadow-sm hover:border-primary/50"
          aria-label="User menu"
        >
          <UserAvatar 
            src={avatarUrl} 
            alt={displayName} 
            displayName={displayName} 
            size="sm" 
            className="h-7 w-7"
          />
          <span className="max-w-[160px] truncate mr-1">{displayName}</span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={8}
          className="z-[100] min-w-[220px] rounded-md border border-border/70 bg-background p-1 text-sm shadow-lg"
        >
        <DropdownMenu.Item asChild>
          <Link href="/dashboard" className="block cursor-pointer rounded px-2 py-1.5 text-foreground hover:bg-muted">
            Dashboard
          </Link>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <Link href="/account" className="block cursor-pointer rounded px-2 py-1.5 text-foreground hover:bg-muted">
            Account
          </Link>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <Link href="/settings/devices" className="block cursor-pointer rounded px-2 py-1.5 text-foreground hover:bg-muted">
            Devices
          </Link>
        </DropdownMenu.Item>
        {isAdmin ? (
          <DropdownMenu.Item asChild>
            <Link href="/admin" className="block cursor-pointer rounded px-2 py-1.5 text-foreground hover:bg-muted">
              Admin
            </Link>
          </DropdownMenu.Item>
        ) : null}
        <DropdownMenu.Separator className="my-1 h-px bg-border/70" />
        <DropdownMenu.Item
          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-foreground hover:bg-muted"
          onSelect={(e) => {
            e.preventDefault();
            switchAccountFormRef.current?.requestSubmit();
          }}
        >
          <Users className="h-4 w-4" />
          Switch Account
        </DropdownMenu.Item>
        <DropdownMenu.Item
          className="block cursor-pointer rounded px-2 py-1.5 text-foreground hover:bg-muted"
          onSelect={(e) => {
            e.preventDefault();
            formRef.current?.requestSubmit();
          }}
        >
          Sign out
        </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>

      {/* Hidden sign-out form to POST to the route handler */}
      <form ref={formRef} action="/auth/sign-out" method="post" hidden />

      {/* Hidden switch account form - signs out and redirects to Planning Center auth */}
      <form ref={switchAccountFormRef} action="/api/auth/planning-center/switch" method="post" hidden />
    </DropdownMenu.Root>
  );
}
